import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const googleReviews = [
  {
    id: 1,
    name: 'Kanakagiri A',
    date: 'January 2026',
    rating: 5,
    text: "I visited Dr.Thamarai's Dental Care few years back to fix my dental issues. The outcome was good and the Dr is very professional and equiped with modern gadgets and tools. Since then it is my prefered clinic for any further dental issues for me.",
    googleUrl: 'https://share.google/JlSN8NWAeWIV8f4NB'
  },
  {
    id: 2,
    name: 'Raja Kanganathan',
    date: 'December 2025',
    rating: 5,
    text: "The Dentist is incredibly polite and made me feel at ease. The dentist explained every step of my treatment with genuine interest, ensuring all my questions were answered. Furthermore, the pricing was normal and transparent, offering great value for the high-quality care.",
    googleUrl: 'https://share.google/gydOs5ffmS5E55Pm3'
  },
  {
    id: 3,
    name: 'Adhithya Raman',
    date: '7 months ago',
    rating: 5,
    text: "Best dental clinic around sanatorium..Dr.Thamarai and Dr sumathy are best dental professionals and hear our issues completely,spends his time and clarify our doubts even the clinic crowded.",
    googleUrl: 'https://share.google/MoyXIueszxXHM274W',
    badge: 'Local Guide'
  },
  {
    id: 4,
    name: 'Vythiyanathan Sundaram',
    date: 'December 2025',
    rating: 5,
    text: "Excellent treatment by Dr Thamarai Selvan, very polite approach, explaining the problem in easy manner and also giving optimum solution. Highly recommend.",
    googleUrl: 'https://share.google/YogW3rLiOojr3wqUH'
  },
  {
    id: 5,
    name: 'Janani JB',
    date: 'December 2025',
    rating: 5,
    text: "Had a great experience by the services and treatment at Thamarai dental clinic. Doctors explained clearly about the procedures of treatment.",
    googleUrl: 'https://share.google/7D6DLdH1xscGWm7Jh'
  }
];

const GoogleReviewsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCards, setVisibleCards] = useState(3);

  // Determine visible cards based on screen width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setVisibleCards(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCards(2);
      } else {
        setVisibleCards(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, googleReviews.length - visibleCards);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(Math.min(index, maxIndex));
  };

  // Auto-slide every 4 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={16}
        className={i < rating ? 'star-filled' : 'star-empty'}
        fill={i < rating ? '#FBBC04' : 'none'}
        stroke={i < rating ? '#FBBC04' : '#ddd'}
      />
    ));
  };

  const truncateText = (text, maxLength = 180) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <section className="google-reviews-section">
      <div className="google-reviews-container">
        {/* Header */}
        <div className="google-reviews-header">
          <div className="google-reviews-badge">
            <svg viewBox="0 0 24 24" width="24" height="24" className="google-icon">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Google Reviews</span>
          </div>
          <h2 className="google-reviews-title">What Our Customers Say on Google</h2>
          <p className="google-reviews-subtitle">Top 5 reviews from verified Google users</p>
        </div>

        {/* Carousel */}
        <div 
          className="google-reviews-carousel"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows */}
          <button 
            className="carousel-arrow carousel-arrow-left"
            onClick={prevSlide}
            aria-label="Previous reviews"
          >
            <ChevronLeft size={24} />
          </button>

          <div className="google-reviews-track-wrapper">
            <div 
              className="google-reviews-track"
              style={{
                transform: `translateX(-${currentIndex * (100 / visibleCards)}%)`,
              }}
            >
              {googleReviews.map((review) => (
                <div 
                  key={review.id} 
                  className="google-review-card"
                  style={{ width: `${100 / visibleCards}%` }}
                >
                  <div className="review-card-inner">
                    {/* Reviewer Info */}
                    <div className="reviewer-info">
                      <div className="reviewer-avatar">
                        {getInitials(review.name)}
                      </div>
                      <div className="reviewer-details">
                        <h4 className="reviewer-name">{review.name}</h4>
                        {review.badge && (
                          <span className="reviewer-badge">{review.badge}</span>
                        )}
                        <span className="review-date">{review.date}</span>
                      </div>
                    </div>

                    {/* Star Rating */}
                    <div className="review-rating">
                      {renderStars(review.rating)}
                    </div>

                    {/* Review Text */}
                    <p className="review-text">
                      {truncateText(review.text)}
                      {review.text.length > 180 && (
                        <a 
                          href={review.googleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="read-more-link"
                        >
                          Read more
                        </a>
                      )}
                    </p>

                    {/* Google Attribution */}
                    <a 
                      href={review.googleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="google-attribution"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span>Reviewed on Google</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button 
            className="carousel-arrow carousel-arrow-right"
            onClick={nextSlide}
            aria-label="Next reviews"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Dot Indicators */}
        <div className="carousel-dots">
          {[...Array(maxIndex + 1)].map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${currentIndex === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="google-reviews-cta">
          <a 
            href="https://share.google/3OnilJZ7D09vGw2JB"
            target="_blank"
            rel="noopener noreferrer"
            className="view-all-reviews-btn"
          >
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            View all reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
